import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Tip } from "@/components/fun-ui/Tip";
import { PhotoProvider, PhotoView } from '@/lib/third-party/react-photo-view/dist/react-photo-view';
import { IconButton } from "@/components/icon-button";
import { ChevronRightIcon, AspectRatioIcon, DownloadIcon, ReverseIcon, XIcon, ZoomInIcon, ZoomOutIcon } from "@/components/fun-ui/icons";
import { Separator } from "@/components/ui/separator";
import { ExtractImage } from "@/stores/slices/paperSlice";
import { MarkdownRenderer } from "../PaperDetail/MarkdownRenderer";
import { downloadBlobFile } from "@/lib/download";
import {
  getExtractChartDownloadFilename,
  getImageExtFromBlob,
} from "./extractChartDownloadFilename";

const THUMBNAIL_LOAD_ROOT_MARGIN = "200px";
const CAPTION_RENDER_ROOT_MARGIN = "120px";
const loadedExtractChartThumbnailUrls = new Set<string>();

function getExtractChartThumbnailUrl(imagePath: string) {
  return `${imagePath}?x-oss-process=image/resize,w_300/quality,q_90`;
}

const LazyExtractChartThumbnail = memo(function LazyExtractChartThumbnail({
  caption,
  imagePath,
  scrollRoot,
}: {
  caption: string;
  imagePath: string;
  scrollRoot: HTMLElement | null;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const thumbnailUrl = imagePath ? getExtractChartThumbnailUrl(imagePath) : "";
  const [shouldLoad, setShouldLoad] = useState(() => (
    !!thumbnailUrl && loadedExtractChartThumbnailUrls.has(thumbnailUrl)
  ));

  useEffect(() => {
    if (!thumbnailUrl) return;
    if (!loadedExtractChartThumbnailUrls.has(thumbnailUrl)) return;
    setShouldLoad(true);
  }, [thumbnailUrl]);

  useEffect(() => {
    if (shouldLoad) return;
    if (!thumbnailUrl) return;

    const node = rootRef.current;
    if (!node) return;
    if (!scrollRoot) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return;
    }

    let observer: IntersectionObserver | null = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        setShouldLoad(true);
        observer?.disconnect();
        observer = null;
      },
      {
        root: scrollRoot,
        rootMargin: THUMBNAIL_LOAD_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(node);

    return () => {
      observer?.disconnect();
    };
  }, [scrollRoot, shouldLoad, thumbnailUrl]);

  const handleImageLoad = useCallback(() => {
    if (!thumbnailUrl) return;
    loadedExtractChartThumbnailUrls.add(thumbnailUrl);
  }, [thumbnailUrl]);

  return (
    <div ref={rootRef} className="flex h-20 min-w-20 items-center justify-center">
      {shouldLoad && thumbnailUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={thumbnailUrl}
          alt={caption}
          className="h-20 max-w-full object-contain"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onLoad={handleImageLoad}
        />
      ) : (
        <div className="h-20 w-20 rounded bg-muted/60" aria-hidden="true" />
      )}
    </div>
  );
});

const LazyExtractChartCaption = memo(function LazyExtractChartCaption({
  caption,
  scrollRoot,
}: {
  caption: string;
  scrollRoot: HTMLElement | null;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [shouldRenderMarkdown, setShouldRenderMarkdown] = useState(false);

  useEffect(() => {
    if (shouldRenderMarkdown) return;

    const node = rootRef.current;
    if (!node) return;
    if (!scrollRoot) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setShouldRenderMarkdown(true);
      return;
    }

    let observer: IntersectionObserver | null = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        setShouldRenderMarkdown(true);
        observer?.disconnect();
        observer = null;
      },
      {
        root: scrollRoot,
        rootMargin: CAPTION_RENDER_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(node);

    return () => {
      observer?.disconnect();
    };
  }, [scrollRoot, shouldRenderMarkdown]);

  const captionMarkdown = shouldRenderMarkdown
    ? <MarkdownRenderer>{caption}</MarkdownRenderer>
    : null;

  return (
    <Tip
      label={captionMarkdown || caption}
      side="bottom"
      contentClassName="max-w-80"
    >
      <div
        ref={rootRef}
        className="min-w-0 max-w-full text-[10px] text-annotation-foreground text-center break-words [overflow-wrap:anywhere] overflow-hidden w-full"
        onMouseEnter={() => setShouldRenderMarkdown(true)}
        onFocusCapture={() => setShouldRenderMarkdown(true)}
      >
        {captionMarkdown || caption}
      </div>
    </Tip>
  );
});

const ExtractChartOverlayCaption = memo(function ExtractChartOverlayCaption({
  caption,
}: {
  caption: string;
}) {
  return <MarkdownRenderer>{caption}</MarkdownRenderer>;
});

export const ExtractChatView = ({
  images = []
}: {
  images?: ExtractImage[]
}) => {
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const handleScrollRootRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  if (!images.length) {
    return <div
      className="absolute inset-0 top-5 bottom-5 px-5 text-annotation-foreground">文献未包含图表内容</div>
  }
  return (
    <PhotoProvider
      maskOpacity={0.8}
      bannerVisible={false}
      overlayRender={(params: any) => {
        const { images: previewImages, index, onClose, onScale, scale, onRotate, rotate, overlay, onIndexChange } = params;
        return (
          <>
            <div
              className="absolute bottom-8 left-0 right-0 text-center text-background z-20 pointer-events-auto flex flex-col">
              <div className="flex justify-center w-full">
                <div className="flex flex-col bg-black/60 rounded-[8px] w-fit backdrop-blur max-w-[520px]">
                  {
                    !!overlay && <div className="text-start text-sm p-4 pb-0">
                      <div className="max-h-[120px] overflow-auto">{overlay}</div>
                    </div>
                  }
                  <div className="flex gap-6 items-center justify-center p-4">
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => onIndexChange(index - 1)}
                      label="上一张"
                    >
                      <ChevronRightIcon size={24} className="rotate-180" />
                    </IconButton>
                    <span className="-mx-6 w-16">{index + 1}/{previewImages.length}</span>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => onIndexChange(index + 1)}
                      label="下一张"
                    >
                      <ChevronRightIcon size={24} />
                    </IconButton>
                    <div className="py-1 h-full w-fit">
                      <Separator className="bg-background/30" orientation="vertical" />
                    </div>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onScale(scale - 0.2);
                      }}
                      label="缩小"
                    >
                      <ZoomOutIcon size={24} />
                    </IconButton>
                    <span className="-mx-2 w-12">{(scale * 100).toFixed(0)}%</span>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onScale(scale + 0.2);
                      }}
                      label="放大"
                    >
                      <ZoomInIcon size={24} />
                    </IconButton>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onScale(1);
                      }}
                      label="重置缩放"
                    >
                      <AspectRatioIcon size={24} />
                    </IconButton>
                    <div className="py-1 h-full w-fit">
                      <Separator className="bg-background/30" orientation="vertical" />
                    </div>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={() => {
                        onRotate(rotate - 90)
                      }}
                      label="向左旋转 90°"
                    >
                      <ReverseIcon size={24} />
                    </IconButton>
                    <IconButton
                      className="h-7 w-7 hover:bg-muted/30 hover:text-background"
                      onClick={async () => {
                        const src = previewImages?.[index]?.src || ''
                        try {
                          if (src) {
                            const response = await fetch(src);
                            const blob = await response.blob();
                            const ext = getImageExtFromBlob(blob);
                            const caption = images[index]?.caption ?? "";
                            downloadBlobFile(
                              blob,
                              getExtractChartDownloadFilename(caption, ext),
                            );
                          }
                        } catch (error) {
                          console.error('下载失败:', error);
                          alert('图片下载失败，请重试');
                        }
                      }}
                      label="下载图片"
                    >
                      <DownloadIcon size={24} />
                    </IconButton>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="absolute right-5 top-5 w-[60px] h-[60px] rounded-full bg-black/60 flex items-center justify-center text-background cursor-pointer hover:bg-black/30 z-20 pointer-events-auto backdrop-blur"
              onClick={() => {
                onClose();
              }}
            >
              <XIcon size={28} />
            </div>
          </>
        );
      }}
    >
      <div
        ref={handleScrollRootRef}
        className="absolute inset-x-0 top-5 bottom-5 px-5 overflow-y-auto overflow-x-hidden no-scrollbar hover:custom-scrollbar"
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-5 py-4">
          {
            images.map(({ caption, imagePath }, index) => {
              const captionText = caption || "";
              return (
                <PhotoView
                  key={`${imagePath || "extract-chart"}-${index}`}
                  src={imagePath}
                  overlay={<ExtractChartOverlayCaption caption={captionText} />}
                >
                  <div className="flex min-w-0 flex-col flex-1">
                    <div
                      className="group flex border rounded justify-center hover:border-primary relative overflow-hidden"
                    >
                      <LazyExtractChartThumbnail
                        caption={captionText}
                        imagePath={imagePath}
                        scrollRoot={scrollRoot}
                      />
                    </div>
                    <LazyExtractChartCaption
                      caption={captionText}
                      scrollRoot={scrollRoot}
                    />
                  </div>
                </PhotoView>
              )
            })
          }
        </div>
      </div>
    </PhotoProvider>
  )
}
